import React, { useEffect, useRef } from "react";
import { cn } from "@/lib/utils";

const ITEM_HEIGHT = 32;
const VISIBLE_ROWS = 2;
const PAD_ROWS = 2;
const MINUTES = Array.from({ length: 24 * 60 }, (_, i) => {
  const h = Math.floor(i / 60);
  const m = i % 60;
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
});

function WheelColumn({ values, selected, onSelect, ariaLabel }) {
  const ref = useRef(null);
  const settleTimer = useRef(null);
  const scrolling = useRef(false);
  const settling = useRef(false);
  const selectedRef = useRef(selected);

  const selectedIndex = Math.max(0, values.indexOf(selected));

  useEffect(() => {
    selectedRef.current = selected;
    if (!ref.current || scrolling.current || settling.current) return;
    ref.current.scrollTop = selectedIndex * ITEM_HEIGHT;
  }, [selected, selectedIndex]);

  useEffect(() => () => {
    if (settleTimer.current) window.clearTimeout(settleTimer.current);
  }, []);

  const indexFromScroll = () => {
    if (!ref.current) return selectedIndex;
    return Math.max(
      0,
      Math.min(values.length - 1, Math.round(ref.current.scrollTop / ITEM_HEIGHT))
    );
  };

  const settle = () => {
    if (!ref.current || settling.current) return;
    settling.current = true;
    scrolling.current = false;

    const index = indexFromScroll();
    const value = values[index];
    const target = index * ITEM_HEIGHT;

    // Only snap after native momentum has stopped. Never write scrollTop from
    // React state while the finger/momentum scroll is active.
    if (Math.abs(ref.current.scrollTop - target) > 0.5) {
      ref.current.scrollTo({ top: target, behavior: "smooth" });
    }

    if (value !== undefined && value !== selectedRef.current) {
      selectedRef.current = value;
      onSelect?.(value);
    }

    window.setTimeout(() => {
      settling.current = false;
    }, 180);
  };

  const scheduleSettle = () => {
    if (settleTimer.current) window.clearTimeout(settleTimer.current);
    settleTimer.current = window.setTimeout(settle, 180);
  };

  const handleScroll = () => {
    if (settling.current) return;
    scrolling.current = true;
    scheduleSettle();
  };

  const handleScrollEnd = () => {
    if (settleTimer.current) window.clearTimeout(settleTimer.current);
    settle();
  };

  const handleWheel = (event) => {
    // Let the browser perform native wheel scrolling. Preventing this event
    // would make desktop testing behave differently from Android touch.
    if (event.deltaY !== 0) scheduleSettle();
  };

  return (
    <div className="relative w-full" aria-label={ariaLabel}>
      <div
        ref={ref}
        className="w-full overflow-y-auto overscroll-contain"
        style={{
          height: `${ITEM_HEIGHT * VISIBLE_ROWS}px`,
          touchAction: "pan-y",
          WebkitOverflowScrolling: "touch",
          scrollSnapType: "none",
          scrollbarWidth: "thin",
        }}
        onScroll={handleScroll}
        onWheel={handleWheel}
        onScrollEnd={handleScrollEnd}
      >
        <div style={{ height: ITEM_HEIGHT * PAD_ROWS }} aria-hidden="true" />
        {values.map((value) => (
          <div
            key={value}
            className={cn(
              "flex select-none items-center justify-center text-sm tabular-nums transition-colors",
              value === selected ? "font-semibold text-foreground" : "text-muted-foreground"
            )}
            style={{ height: ITEM_HEIGHT }}
          >
            {value}
          </div>
        ))}
        <div style={{ height: ITEM_HEIGHT * PAD_ROWS }} aria-hidden="true" />
      </div>

      <div
        className="pointer-events-none absolute left-0 right-0 top-1/2 -translate-y-1/2 rounded-md border bg-background/70"
        style={{ height: ITEM_HEIGHT }}
        aria-hidden="true"
      />
    </div>
  );
}

/**
 * Touch-friendly wheel time selector used by every Start Time / Finish Time
 * field in DrillLogPro.
 *
 * The scrolling surface is deliberately native browser scrolling: Android
 * receives the finger flick, momentum and deceleration without React trying
 * to control scrollTop. Selection is committed only after scrolling settles,
 * then the wheel makes a small smooth final snap to the nearest minute.
 */
export default function TimeScroller({ value = "", onChange, className, disabled = false }) {
  const safeValue = /^([01]\d|2[0-3]):([0-5]\d)$/.test(String(value))
    ? String(value)
    : "00:00";

  const selectedRef = useRef(safeValue);

  useEffect(() => {
    selectedRef.current = safeValue;
  }, [safeValue]);

  return (
    <div
      className={cn(
        "relative flex h-16 w-full items-center overflow-hidden rounded-xl border bg-background shadow-sm",
        disabled && "opacity-50",
        className
      )}
      aria-disabled={disabled}
    >
      <WheelColumn
        values={MINUTES}
        selected={selectedRef.current}
        onSelect={(next) => {
          if (!disabled) onChange?.(next);
        }}
        ariaLabel="Time selector"
      />
    </div>
  );
}
